import { VOICE_MAX_MS } from '@bits/domain';
import { pickRecorderType } from './chat-model';

export interface VoiceRecording {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'denied' | 'unsupported';

// Wraps MediaRecorder so the composer only sees start / stop / cancel and an
// elapsed clock. The clock is the duration the server stores: Chromium's
// WebM has no duration header, so the file itself cannot say how long it is.
export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private limit: ReturnType<typeof setTimeout> | null = null;
  private stopping: ((recording: VoiceRecording | null) => void) | null = null;

  static supported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  constructor(private onPhase: (phase: RecorderPhase) => void) {}

  async start(): Promise<void> {
    if (!VoiceRecorder.supported()) {
      this.onPhase('unsupported');
      return;
    }
    const mimeType = pickRecorderType((t) => MediaRecorder.isTypeSupported(t));
    if (!mimeType) {
      this.onPhase('unsupported');
      return;
    }
    this.onPhase('starting');
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.onPhase('denied');
      return;
    }
    this.chunks = [];
    const recorder = new MediaRecorder(this.stream, { mimeType });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    recorder.onstop = () => {
      const durationMs = Math.max(1, Date.now() - this.startedAt);
      const blob = new Blob(this.chunks, { type: mimeType });
      const result = this.stopping;
      this.release();
      result?.(blob.size > 0 ? { blob, mimeType, durationMs } : null);
    };
    this.recorder = recorder;
    this.startedAt = Date.now();
    recorder.start(1_000);
    this.limit = setTimeout(() => void this.stop(), VOICE_MAX_MS);
    this.onPhase('recording');
  }

  elapsedMs(): number {
    return this.recorder ? Date.now() - this.startedAt : 0;
  }

  /** Resolves with the recording, or null when nothing was captured. */
  stop(): Promise<VoiceRecording | null> {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') {
      this.release();
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      this.stopping = resolve;
      recorder.stop();
    });
  }

  cancel(): void {
    const recorder = this.recorder;
    this.stopping = null;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    this.release();
  }

  private release(): void {
    if (this.limit) clearTimeout(this.limit);
    this.limit = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.stopping = null;
    this.onPhase('idle');
  }
}
